import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError, exportCsv } from "@/lib/api";
import { inr, num } from "@/lib/format";
import { PageLoader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { BookPlus, Library, Plus, X, Pencil, Trash2, Loader2, Printer, Upload, Download, FileSpreadsheet, CheckCircle2, Search } from "lucide-react";
import { fileToDataUrl } from "@/lib/image";

const COVERS = [
  "https://images.unsplash.com/photo-1716892001555-c05f13f4362a?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  "https://images.unsplash.com/photo-1519764340700-3db40311f21e?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
];

const DEFAULT_PLATFORMS = ["Amazon", "Manda Store", "Flipkart", "Kindle", "Google Play"];

const emptyForm = () => ({
  title: "",
  author_user_id: "",
  cover_url: COVERS[0],
  printing_rate: "",
  platforms: DEFAULT_PLATFORMS.map((name) => ({ name, royalty_per_copy: "" })),
});

export default function Books() {
  const qc = useQueryClient();
  const { data: books, isLoading } = useQuery({
    queryKey: ["books"],
    queryFn: async () => (await api.get("/admin/books")).data,
  });
  const { data: authors } = useQuery({
    queryKey: ["authors"],
    queryFn: async () => (await api.get("/admin/authors")).data,
  });

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [search, setSearch] = useState("");

  const bulkUpload = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post("/admin/books/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (d) => {
      setBulkResult(d);
      toast.success(`${d.processed} book(s) imported`);
      qc.invalidateQueries({ queryKey: ["books"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const downloadBooksTemplate = async () => {
    try {
      const res = await api.get("/admin/books/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "books_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const openNew = () => {
    setEditId(null);
    setForm(emptyForm());
    setOpen(true);
  };
  const openEdit = (b) => {
    setEditId(b.id);
    setForm({
      title: b.title,
      author_user_id: b.author_user_id,
      cover_url: b.cover_url || COVERS[0],
      printing_rate: String(b.printing_rate),
      platforms: b.platforms.map((p) => ({ name: p.name, royalty_per_copy: String(p.royalty_per_copy) })),
    });
    setOpen(true);
  };

  const payload = () => ({
    title: form.title.trim(),
    author_user_id: form.author_user_id,
    cover_url: form.cover_url,
    printing_rate: parseFloat(form.printing_rate) || 0,
    platforms: form.platforms
      .filter((p) => p.name.trim())
      .map((p) => ({ name: p.name.trim(), royalty_per_copy: parseFloat(p.royalty_per_copy) || 0 })),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (editId) return (await api.put(`/admin/books/${editId}`, payload())).data;
      return (await api.post("/admin/books", payload())).data;
    },
    onSuccess: () => {
      toast.success(editId ? "Book updated" : "Book added");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["books"] });
      qc.invalidateQueries({ queryKey: ["authors"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/books/${id}`)).data,
    onSuccess: () => {
      toast.success("Book deleted");
      qc.invalidateQueries({ queryKey: ["books"] });
    },
  });

  const valid = form.title && form.author_user_id && form.printing_rate && form.platforms.some((p) => p.name.trim() && p.royalty_per_copy);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Book catalogue</p>
          <p className="text-sm text-muted-foreground mt-1">Assign books to authors with per-platform royalty and printing rates.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" data-testid="export-books" onClick={() => exportCsv("/admin/books/export", "books.csv").catch(() => toast.error("Export failed"))}>
            <Download className="h-4 w-4" /> Export
          </Button>
          <Button variant="outline" className="gap-2" data-testid="bulk-book-button" onClick={() => { setBulkResult(null); setBulkOpen(true); }} disabled={!authors?.length}>
            <Upload className="h-4 w-4" /> Bulk Import
          </Button>
          <Button data-testid="add-book-button" onClick={openNew} className="gap-2" disabled={!authors?.length}>
            <BookPlus className="h-4 w-4" /> Add Book
          </Button>
        </div>
      </div>

      {!authors?.length && (
        <p className="text-sm text-amber-700 dark:text-amber-400 bg-amber-100/50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-md px-3 py-2">
          Create an author first before adding books.
        </p>
      )}

      {isLoading ? (
        <PageLoader />
      ) : books.length === 0 ? (
        <EmptyState icon={Library} title="No books yet" desc="Add a book and assign it to an author to begin tracking sales." />
      ) : (
        <>
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="book-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search books by title or author…" className="pl-9" />
          </div>
          {(() => {
            const q = search.trim().toLowerCase();
            const filtered = q ? books.filter((b) => b.title.toLowerCase().includes(q) || (b.author_name || "").toLowerCase().includes(q)) : books;
            if (filtered.length === 0) return <EmptyState icon={Search} title="No matches" desc="No books match your search." />;
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filtered.map((b) => (
            <Card key={b.id} data-testid={`book-card-${b.id}`} className="overflow-hidden hover:-translate-y-1 hover:shadow-md transition-transform duration-200">
              <div className="flex gap-4 p-5">
                <img src={b.cover_url || COVERS[0]} alt={b.title} className="h-24 w-16 object-cover rounded-md border border-border shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-head font-semibold leading-tight line-clamp-2">{b.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">by {b.author_name}</p>
                  <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground">
                    <Printer className="h-3.5 w-3.5" /> Print rate {inr(b.printing_rate)}/copy
                  </div>
                </div>
              </div>
              <div className="px-5 pb-3 flex flex-wrap gap-1.5">
                {b.platforms.map((p) => (
                  <Badge key={p.name} variant="secondary" className="font-normal">
                    {p.name} · ₹{num(p.royalty_per_copy)}/copy
                  </Badge>
                ))}
              </div>
              <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-muted/30">
                <div className="text-xs">
                  <span className="tabular-nums font-medium">{num(b.total_copies)}</span>
                  <span className="text-muted-foreground"> copies · </span>
                  <span className="tabular-nums font-medium text-primary">{inr(b.total_profit)}</span>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-book-${b.id}`} onClick={() => openEdit(b)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`delete-book-${b.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete "{b.title}"?</AlertDialogTitle>
                        <AlertDialogDescription>All sales records for this book will also be removed.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => del.mutate(b.id)}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </Card>
          ))}
        </div>
            );
          })()}
        </>
      )}

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-head">Bulk import books</DialogTitle>
            <DialogDescription>One row per book. Columns: user_id, title, printing_rate, then one column per platform holding the royalty per copy — <code>Amazon, Flipkart, Kindle</code>… Leave a platform's cell blank if the book isn't sold there.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Button variant="outline" className="gap-2" data-testid="download-books-template" onClick={downloadBooksTemplate}>
              <Download className="h-4 w-4" /> Download template
            </Button>
            <label htmlFor="bulk-book-file" className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-xl py-10 cursor-pointer hover:bg-muted/40 transition-colors">
              {bulkUpload.isPending ? <Loader2 className="h-7 w-7 text-primary animate-spin" /> : <FileSpreadsheet className="h-7 w-7 text-muted-foreground" strokeWidth={1.5} />}
              <p className="text-sm font-medium">Choose a .csv or .xlsx file</p>
              <input id="bulk-book-file" type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="bulk-book-file-input"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) { setBulkResult(null); bulkUpload.mutate(f); } e.target.value = ""; }} />
            </label>
            {bulkResult && (
              <div data-testid="bulk-book-result" className="rounded-lg border border-border p-4 space-y-2 text-sm">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> <span className="font-medium">{bulkResult.processed}</span> book(s) imported.</div>
                {bulkResult.errors?.length > 0 && (
                  <ul className="text-xs text-muted-foreground list-disc pl-6 max-h-40 overflow-y-auto">
                    {bulkResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-head">{editId ? "Edit book" : "Add new book"}</DialogTitle>
            <DialogDescription>Set the platforms it sells on and the royalty profit per copy for each.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input data-testid="book-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="The Midnight Library" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Author</Label>
                <Select value={form.author_user_id} onValueChange={(v) => setForm({ ...form, author_user_id: v })}>
                  <SelectTrigger data-testid="book-author-select"><SelectValue placeholder="Select author" /></SelectTrigger>
                  <SelectContent>
                    {authors?.map((a) => (
                      <SelectItem key={a.user_id} value={a.user_id}>{a.name} (@{a.user_id})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Printing rate (₹/copy)</Label>
                <Input data-testid="book-print-rate" type="number" min="0" value={form.printing_rate} onChange={(e) => setForm({ ...form, printing_rate: e.target.value })} placeholder="120" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Cover</Label>
              <div className="flex items-center gap-3 flex-wrap">
                {COVERS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, cover_url: c })}
                    className={`rounded-md overflow-hidden border-2 transition-colors ${form.cover_url === c ? "border-primary" : "border-transparent opacity-70"}`}
                  >
                    <img src={c} alt="cover" className="h-16 w-11 object-cover" />
                  </button>
                ))}
                {form.cover_url && !COVERS.includes(form.cover_url) && (
                  <div className="rounded-md overflow-hidden border-2 border-primary">
                    <img src={form.cover_url} alt="uploaded cover" className="h-16 w-11 object-cover" />
                  </div>
                )}
                <input
                  id="cover-file" type="file" accept="image/*" className="hidden" data-testid="book-cover-upload"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    try {
                      const url = await fileToDataUrl(f, 500);
                      setForm((prev) => ({ ...prev, cover_url: url }));
                    } catch (err) {
                      toast.error(err.message);
                    }
                    e.target.value = "";
                  }}
                />
                <Button type="button" variant="outline" size="sm" className="gap-1.5 h-16" onClick={() => document.getElementById("cover-file").click()}>
                  <Upload className="h-4 w-4" /> Upload
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Platforms — royalty / copy</Label>
                <Button
                  type="button" variant="ghost" size="sm" className="h-7 gap-1 text-xs"
                  data-testid="add-platform-row"
                  onClick={() => setForm({ ...form, platforms: [...form.platforms, { name: "", royalty_per_copy: "" }] })}
                >
                  <Plus className="h-3.5 w-3.5" /> Add platform
                </Button>
              </div>
              <div className="space-y-2.5">
                {form.platforms.map((p, i) => (
                  <div key={i} className="flex flex-wrap gap-2 items-center border border-border rounded-lg p-2.5">
                    <Input
                      data-testid={`platform-name-${i}`}
                      placeholder="e.g. Amazon"
                      className="flex-1 min-w-[120px]"
                      value={p.name}
                      onChange={(e) => {
                        const next = [...form.platforms];
                        next[i].name = e.target.value;
                        setForm({ ...form, platforms: next });
                      }}
                    />
                    <Input
                      data-testid={`platform-royalty-${i}`}
                      type="number" min="0" placeholder="Royalty ₹"
                      className="w-28"
                      value={p.royalty_per_copy}
                      onChange={(e) => {
                        const next = [...form.platforms];
                        next[i].royalty_per_copy = e.target.value;
                        setForm({ ...form, platforms: next });
                      }}
                    />
                    {form.platforms.length > 1 && (
                      <Button
                        type="button" variant="ghost" size="icon" className="shrink-0 text-muted-foreground h-8 w-8"
                        onClick={() => setForm({ ...form, platforms: form.platforms.filter((_, idx) => idx !== i) })}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Royalty is the final profit per copy for the author on each platform.</p>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-book-button" onClick={() => save.mutate()} disabled={!valid || save.isPending}>
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : editId ? "Save changes" : "Add book"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
